/** Which actions an invite offers, by status and permission — the one rule set the grid's row
 *  menu and the drawer's Actions menu both read (plan §15 "Row actions (by status)").
 *
 *  The backend is authoritative (it answers 409 with a typed code); this only hides what cannot
 *  succeed. Every action except opening the tenant needs `console_onboarding_invites_manage` —
 *  the link itself included, since it is a working credential until the invite is used. */
import type { InviteListItem, InviteStatus } from '@/api/onboardingInvites';

export type InviteActionId =
  | 'copy'
  | 'qr'
  | 'send'
  | 'release'
  | 'rotate'
  | 'revoke'
  | 'reinstate'
  | 'edit'
  | 'expiry'
  | 'open_onboarding';

/** Not used and not revoked: the link can still be (made to) work. */
const OPEN: ReadonlySet<InviteStatus> = new Set(['available', 'claimed', 'expired']);
/** The link works right now. */
const WORKING: ReadonlySet<InviteStatus> = new Set(['available', 'claimed']);
/** Editable: anything not used yet (§5.3 — after `used` the row is read-only). */
const EDITABLE: ReadonlySet<InviteStatus> = new Set(['available', 'claimed', 'expired', 'revoked']);

export interface InviteActionPerms {
  /** console_onboarding_invites_manage */
  canManage: boolean;
  /** console_top_view — the tenant's onboarding page route gate. */
  canOpenTenant: boolean;
}

type InviteLike = Pick<InviteListItem, 'status' | 'bound' | 'send_count' | 'claim' | 'tenant'>;

/** The actions this invite offers this operator, in menu order. */
export function inviteActionsFor(invite: InviteLike, perms: InviteActionPerms): InviteActionId[] {
  const s = invite.status;
  const out: InviteActionId[] = [];
  if (perms.canManage) {
    if (OPEN.has(s)) out.push('copy', 'qr');
    if (invite.bound && WORKING.has(s)) out.push('send');
    if (invite.claim.claimed && OPEN.has(s)) out.push('release');
    if (OPEN.has(s)) out.push('rotate', 'revoke');
    if (s === 'revoked') out.push('reinstate');
    if (EDITABLE.has(s)) out.push('edit');
    if (OPEN.has(s)) out.push('expiry');
  }
  if (invite.tenant && perms.canOpenTenant) out.push('open_onboarding');
  return out;
}

/** The menu wording of an action for this invite. */
export function inviteActionLabel(action: InviteActionId, invite: InviteLike): string {
  switch (action) {
    case 'copy':
      return 'Copy link';
    case 'qr':
      return 'QR code';
    case 'send':
      return invite.send_count > 0 ? 'Resend e-mail' : 'Send e-mail';
    case 'release':
      return 'Release claim';
    case 'rotate':
      return 'Rotate code';
    case 'revoke':
      return 'Revoke';
    case 'reinstate':
      return 'Reinstate';
    case 'edit':
      return 'Edit';
    case 'expiry':
      return invite.status === 'expired' ? 'Extend expiry' : 'Change expiry';
    case 'open_onboarding':
      return 'Open tenant onboarding';
  }
}

/** The tenant's easy-onboarding page (console route `tour-operators/:id/onboard`). */
export const tenantOnboardingPath = (tenantId: number) => `/tour-operators/${tenantId}/onboard`;
